import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Users,
  Presentation,
  CheckCircle,
  Star,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { services, stats, testimonials, spaces } from "../data/mock";
import portada from "@/assets/img/mk_edificio.jpeg";
import video from "@/assets/img/comercial.mp4";

const HomePage = () => {
  const featuredServices = services.slice(0, 3);
  const featuredSpaces = spaces.filter((s) => s.available).slice(0, 3);

  // Estado para el video
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Building2: Building2,
      Users: Users,
      Presentation: Presentation,
    };
    const Icon = icons[iconName] || Building2;
    return <Icon className="w-8 h-8" />;
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section with Image */}
      <section className="relative h-screen flex items-center justify-center">
        {/* Image Background */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={portada}
            alt="Corporativo MK Building"
            className="absolute w-full h-full object-cover object-center scale-105"
          />
          {/* Gradient Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-900/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-slate-900/30" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Espacios que
              <span className="text-sky-400"> impulsan </span>
              tu negocio
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
              Descubre oficinas y espacios de coworking premium diseñados para
              potenciar la productividad y el crecimiento de tu empresa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/locales">
                <Button
                  size="lg"
                  className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Ver Espacios Disponibles
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contacto">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg backdrop-blur-sm"
                >
                  Agendar Visita
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/60 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-2xl bg-slate-50 hover:bg-sky-50 transition-colors duration-300"
              >
                <div className="text-4xl lg:text-5xl font-bold text-sky-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Conoce Nuestras Instalaciones
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Descubre por qué las mejores empresas eligen Corporativo MK para
              establecer sus operaciones.
            </p>
          </div>

          {/* Video Container */}
          <div className="relative max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-800 aspect-video">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                poster={portada}
                muted={isMuted}
                loop
                playsInline
              >
                <source src={video} type="video/mp4" />
                Tu navegador no soporta el elemento de video.
              </video>

              {/* Overlay cuando no está reproduciendo */}
              {!isPlaying && (
                <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
                  <button
                    onClick={togglePlay}
                    className="w-24 h-24 rounded-full bg-sky-500 hover:bg-sky-600 flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-2xl"
                  >
                    <Play className="w-10 h-10 text-white ml-1" fill="white" />
                  </button>
                </div>
              )}

              {/* Controles del video */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-slate-900/80 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={togglePlay}
                      className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors backdrop-blur-sm"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5 text-white" />
                      ) : (
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      )}
                    </button>
                    <button
                      onClick={toggleMute}
                      className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors backdrop-blur-sm"
                    >
                      {isMuted ? (
                        <VolumeX className="w-5 h-5 text-white" />
                      ) : (
                        <Volume2 className="w-5 h-5 text-white" />
                      )}
                    </button>
                  </div>
                  <span className="text-white/80 text-sm font-medium">
                    Corporativo MK - Tour Virtual
                  </span>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-sky-500/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-sky-500/20 rounded-full blur-2xl" />
          </div>

          {/* Video Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-4xl mx-auto">
            {[
              {
                title: "Ubicación Premium",
                desc: "En el corazón de la ciudad",
              },
              {
                title: "Instalaciones Modernas",
                desc: "Tecnología de última generación",
              },
              { title: "Ambiente Profesional", desc: "Diseñado para el éxito" },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Soluciones a tu medida
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Ofrecemos una amplia gama de servicios diseñados para satisfacer
              las necesidades de cada empresa.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredServices.map((service) => (
              <Card
                key={service.id}
                className="group bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl bg-sky-100 text-sky-600 flex items-center justify-center mb-6 group-hover:bg-sky-500 group-hover:text-white transition-all duration-300">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-center text-sm text-slate-600"
                      >
                        <CheckCircle className="w-4 h-4 text-sky-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/servicios">
              <Button
                variant="outline"
                className="border-sky-500 text-sky-600 hover:bg-sky-50 px-8"
              >
                Ver Todos los Servicios
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Spaces */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Espacios Destacados
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Conoce nuestros espacios más populares disponibles para tu
              empresa.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredSpaces.map((space) => (
              <Card
                key={space.id}
                className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={space.image}
                    alt={space.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-sky-500 text-white text-sm font-medium rounded-full">
                      {space.type}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    {space.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    {space.location}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sky-600 font-bold">
                      {space.price}
                    </span>
                    <span className="text-slate-500 text-sm">
                      {space.capacity}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/locales">
              <Button className="bg-sky-500 hover:bg-sky-600 text-white px-8">
                Ver Todos los Espacios
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Lo que dicen nuestros clientes
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              La satisfacción de nuestros clientes es nuestra mayor recompensa.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="bg-slate-800/50 border-slate-700 backdrop-blur-sm"
              >
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 text-amber-400 fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-slate-300 mb-6 leading-relaxed italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <div className="font-semibold text-white">
                      {testimonial.name}
                    </div>
                    <div className="text-sky-400 text-sm">
                      {testimonial.position}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sky-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            ¿Listo para encontrar tu espacio ideal?
          </h2>
          <p className="text-xl text-sky-100 mb-10 max-w-2xl mx-auto">
            Agenda una visita gratuita y descubre cómo podemos impulsar el
            crecimiento de tu empresa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button
                size="lg"
                className="bg-white text-sky-600 hover:bg-sky-50 px-8 py-6 text-lg shadow-xl"
              >
                Agendar Visita Gratuita
              </Button>
            </Link>
            <Link to="/locales">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                Explorar Espacios
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
